import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const palette = {
  panel: 'rgba(18, 20, 30, 0.9)',
  panelSoft: 'rgba(16, 18, 28, 0.85)',
  border: 'rgba(255, 255, 255, 0.12)',
  ink: '#f5f8ff',
  muted: 'rgba(245, 248, 255, 0.7)',
  glow: '0 40px 90px -55px rgba(0, 0, 0, 0.85)'
};

const MonitoringContainer = styled.div`
  display: grid;
  gap: 1.5rem;
  color: ${palette.ink};
  font-size: 0.95rem;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.1rem;
`;

const MetricCard = styled.div`
  background: ${palette.panelSoft};
  border-radius: 18px;
  padding: 1.4rem;
  box-shadow: ${palette.glow};
  border: 1px solid ${palette.border};
  
  .metric-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.1rem;
    
    .title {
      display: flex;
      align-items: center;
      gap: 0.55rem;
      
      .icon {
        font-size: 1.3rem;
      }
      
      h3 {
        margin: 0;
        color: ${palette.ink};
        font-size: 1rem;
      }
    }
    
    .status {
      padding: 0.2rem 0.6rem;
      border-radius: 10px;
      font-size: 0.7rem;
      font-weight: 600;
      
      &.healthy {
        background: rgba(52, 199, 89, 0.2);
        color: #34d399;
      }
      
      &.warning {
        background: rgba(255, 159, 10, 0.2);
        color: #ffb347;
      }
      
      &.critical {
        background: rgba(255, 69, 58, 0.2);
        color: #ff7b72;
      }
    }
  }
`;

const ProgressBar = styled.div`
  background: rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  height: 10px;
  overflow: hidden;
  margin-bottom: 0.75rem;
  
  .progress-fill {
    height: 100%;
    border-radius: 8px;
    transition: all 0.3s ease;
    background: ${props => {
    if (props.value > 80) return 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)';
    if (props.value > 60) return 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)';
    return 'linear-gradient(90deg, #10b981 0%, #059669 100%)';
  }};
    width: ${props => props.value}%;
  }
`;

const MetricValue = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.4rem;
  
  .value {
    font-size: 1.3rem;
    font-weight: 700;
    color: ${palette.ink};
  }
  
  .unit {
    font-size: 0.85rem;
    color: ${palette.muted};
  }
`;

const LiveChart = styled.div`
  height: 160px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 14px;
  padding: 0.8rem;
  position: relative;
  overflow: hidden;
  
  .chart-line {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: linear-gradient(
      to bottom,
      rgba(102, 126, 234, 0.3) 0%,
      rgba(102, 126, 234, 0.1) 50%,
      transparent 100%
    );
    border-top: 2px solid #667eea;
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
  
  .chart-label {
    position: absolute;
    top: 1rem;
    left: 1rem;
    font-size: 0.7rem;
    color: ${palette.muted};
    font-weight: 600;
  }
  
  .chart-value {
    position: absolute;
    top: 1rem;
    right: 1rem;
    font-size: 1.05rem;
    font-weight: 700;
    color: ${palette.ink};
  }
`;

const AlertsPanel = styled.div`
  background: ${palette.panel};
  border-radius: 20px;
  box-shadow: ${palette.glow};
  border: 1px solid ${palette.border};
  overflow: hidden;
`;

const AlertsHeader = styled.div`
  padding: 1.2rem 1.6rem;
  border-bottom: 1px solid ${palette.border};
  background: linear-gradient(135deg, rgba(10, 84, 220, 0.9), rgba(3, 6, 14, 0.95));
  color: ${palette.ink};
  
  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    h3 {
      margin: 0;
      display: flex;
      align-items: center;
      gap: 0.6rem;
      
      .icon {
        font-size: 1.2rem;
      }
    }
    
    .alert-count {
      padding: 0.4rem 0.85rem;
      background: rgba(255, 255, 255, 0.15);
      border-radius: 16px;
      font-size: 0.8rem;
      font-weight: 600;
    }
  }
`;

const AlertsList = styled.div`
  max-height: 320px;
  overflow-y: auto;
  
  .alert-item {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
    padding: 1.1rem 1.4rem;
    border-bottom: 1px solid ${palette.border};
    
    &:last-child {
      border-bottom: none;
    }
    
    .alert-icon {
      width: 34px;
      height: 34px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.2rem;
      flex-shrink: 0;
      
      &.critical {
        background: rgba(255, 69, 58, 0.2);
        color: #ff7b72;
      }
      
      &.warning {
        background: rgba(255, 159, 10, 0.2);
        color: #ffb347;
      }
      
      &.info {
        background: rgba(90, 200, 255, 0.2);
        color: ${palette.accent};
      }
    }
    
    .alert-content {
      flex: 1;
      
      .alert-title {
        font-weight: 600;
        color: ${palette.ink};
        margin-bottom: 0.25rem;
      }
      
      .alert-message {
        color: ${palette.muted};
        font-size: 0.85rem;
        margin-bottom: 0.4rem;
      }
      
      .alert-time {
        font-size: 0.8rem;
        color: rgba(245, 248, 255, 0.5);
      }
    }
    
    .alert-actions {
      display: flex;
      gap: 0.5rem;
      
      button {
        padding: 0.25rem 0.75rem;
        border: none;
        border-radius: 6px;
        font-size: 0.8rem;
        font-weight: 600;
        cursor: pointer;
        
        &.dismiss {
          background: rgba(255, 255, 255, 0.08);
          color: ${palette.ink};
          
          &:hover {
            background: rgba(255, 255, 255, 0.14);
          }
        }
        
        &.resolve {
          background: rgba(16, 185, 129, 0.25);
          color: #34d399;
          
          &:hover {
            background: rgba(16, 185, 129, 0.35);
          }
        }
      }
    }
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.9rem;
`;

const ServiceCard = styled.div`
  background: ${palette.panel};
  border-radius: 16px;
  padding: 1.2rem;
  box-shadow: ${palette.glow};
  border: 1px solid ${palette.border};
  
  .service-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    
    .service-name {
      font-weight: 600;
      color: ${palette.ink};
    }
    
    .service-status {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      
      &.running {
        background: #34d399;
        animation: pulse 2s infinite;
      }
      
      &.stopped {
        background: #ff7b72;
      }
      
      &.warning {
        background: #ffb347;
      }
    }
  }
  
  .service-metrics {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
    font-size: 0.8rem;
    
    .metric {
      display: flex;
      justify-content: space-between;
      
      .label {
        color: ${palette.muted};
      }
      
      .value {
        font-weight: 600;
        color: ${palette.ink};
      }
    }
  }
`;

const SystemMonitoring = () => {
  const [systemMetrics, setSystemMetrics] = useState({
    cpu: 42,
    memory: 68,
    disk: 35,
    network: 15
  });

  const [alerts, setAlerts] = useState([
    {
      id: 1,
      type: 'warning',
      title: 'High Memory Usage',
      message: 'Memory usage has exceeded 70% for the past 10 minutes',
      time: '5 minutes ago',
      resolved: false
    },
    {
      id: 2,
      type: 'info',
      title: 'Database Backup Completed',
      message: 'Daily database backup completed successfully',
      time: '1 hour ago',
      resolved: false
    },
    {
      id: 3,
      type: 'critical',
      title: 'Payment Gateway Timeout',
      message: 'Payment processing is experiencing delays',
      time: '2 hours ago',
      resolved: false
    }
  ]);

  const [services] = useState([
    { name: 'Web Server', status: 'running', cpu: 25, memory: 512 },
    { name: 'Database', status: 'running', cpu: 15, memory: 1024 },
    { name: 'Payment Gateway', status: 'warning', cpu: 8, memory: 256 },
    { name: 'Email Service', status: 'running', cpu: 5, memory: 128 },
    { name: 'File Storage', status: 'running', cpu: 12, memory: 384 },
    { name: 'Analytics Engine', status: 'running', cpu: 35, memory: 768 }
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      // Simulate real-time metrics updates
      setSystemMetrics(prev => ({
        cpu: Math.max(10, Math.min(90, prev.cpu + (Math.random() - 0.5) * 10)),
        memory: Math.max(20, Math.min(85, prev.memory + (Math.random() - 0.5) * 8)),
        disk: Math.max(10, Math.min(95, prev.disk + (Math.random() - 0.5) * 3)),
        network: Math.max(5, Math.min(80, prev.network + (Math.random() - 0.5) * 15))
      }));
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const getMetricStatus = (value, thresholds = { warning: 70, critical: 85 }) => {
    if (value >= thresholds.critical) return 'critical';
    if (value >= thresholds.warning) return 'warning';
    return 'healthy';
  };

  const handleAlertAction = (alertId, action) => {
    if (action === 'dismiss' || action === 'resolve') {
      setAlerts(prev => prev.filter(alert => alert.id !== alertId));
    }
  };

  const formatUptime = (hours) => {
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  };

  return (
    <MonitoringContainer>
      {/* System Metrics */}
      <MetricsGrid>
        <MetricCard>
          <div className="metric-header">
            <div className="title">
              <span className="icon">🖥️</span>
              <h3>CPU Usage</h3>
            </div>
            <div className={`status ${getMetricStatus(systemMetrics.cpu)}`}>
              {systemMetrics.cpu > 85 ? 'Critical' : systemMetrics.cpu > 70 ? 'Warning' : 'Healthy'}
            </div>
          </div>
          <MetricValue>
            <span className="value">{systemMetrics.cpu.toFixed(1)}</span>
            <span className="unit">%</span>
          </MetricValue>
          <ProgressBar value={systemMetrics.cpu}>
            <div className="progress-fill"></div>
          </ProgressBar>
          <LiveChart>
            <div className="chart-label">Real-time CPU</div>
            <div className="chart-value">{systemMetrics.cpu.toFixed(1)}%</div>
            <div className="chart-line" style={{ height: `${systemMetrics.cpu}%` }}></div>
          </LiveChart>
        </MetricCard>

        <MetricCard>
          <div className="metric-header">
            <div className="title">
              <span className="icon">💾</span>
              <h3>Memory Usage</h3>
            </div>
            <div className={`status ${getMetricStatus(systemMetrics.memory)}`}>
              {systemMetrics.memory > 85 ? 'Critical' : systemMetrics.memory > 70 ? 'Warning' : 'Healthy'}
            </div>
          </div>
          <MetricValue>
            <span className="value">{systemMetrics.memory.toFixed(1)}</span>
            <span className="unit">%</span>
          </MetricValue>
          <ProgressBar value={systemMetrics.memory}>
            <div className="progress-fill"></div>
          </ProgressBar>
          <LiveChart>
            <div className="chart-label">Memory Usage</div>
            <div className="chart-value">{systemMetrics.memory.toFixed(1)}%</div>
            <div className="chart-line" style={{ height: `${systemMetrics.memory}%` }}></div>
          </LiveChart>
        </MetricCard>

        <MetricCard>
          <div className="metric-header">
            <div className="title">
              <span className="icon">💽</span>
              <h3>Disk Usage</h3>
            </div>
            <div className={`status ${getMetricStatus(systemMetrics.disk, { warning: 80, critical: 95 })}`}>
              {systemMetrics.disk > 95 ? 'Critical' : systemMetrics.disk > 80 ? 'Warning' : 'Healthy'}
            </div>
          </div>
          <MetricValue>
            <span className="value">{systemMetrics.disk.toFixed(1)}</span>
            <span className="unit">%</span>
          </MetricValue>
          <ProgressBar value={systemMetrics.disk}>
            <div className="progress-fill"></div>
          </ProgressBar>
          <LiveChart>
            <div className="chart-label">Disk Usage</div>
            <div className="chart-value">{systemMetrics.disk.toFixed(1)}%</div>
            <div className="chart-line" style={{ height: `${systemMetrics.disk}%` }}></div>
          </LiveChart>
        </MetricCard>

        <MetricCard>
          <div className="metric-header">
            <div className="title">
              <span className="icon">🌐</span>
              <h3>Network I/O</h3>
            </div>
            <div className={`status ${getMetricStatus(systemMetrics.network, { warning: 60, critical: 80 })}`}>
              {systemMetrics.network > 80 ? 'Critical' : systemMetrics.network > 60 ? 'Warning' : 'Healthy'}
            </div>
          </div>
          <MetricValue>
            <span className="value">{systemMetrics.network.toFixed(1)}</span>
            <span className="unit">Mbps</span>
          </MetricValue>
          <ProgressBar value={systemMetrics.network}>
            <div className="progress-fill"></div>
          </ProgressBar>
          <LiveChart>
            <div className="chart-label">Network I/O</div>
            <div className="chart-value">{systemMetrics.network.toFixed(1)} Mbps</div>
            <div className="chart-line" style={{ height: `${systemMetrics.network}%` }}></div>
          </LiveChart>
        </MetricCard>
      </MetricsGrid>

      {/* Services Status */}
      <AlertsPanel>
        <AlertsHeader>
          <div className="header-content">
            <h3>
              <span className="icon">⚙️</span>
              System Services
            </h3>
            <div className="alert-count">
              {services.filter(s => s.status === 'running').length}/{services.length} Running
            </div>
          </div>
        </AlertsHeader>

        <div style={{ padding: '2rem' }}>
          <ServicesGrid>
            {services.map((service, index) => (
              <ServiceCard key={index}>
                <div className="service-header">
                  <div className="service-name">{service.name}</div>
                  <div className={`service-status ${service.status}`}></div>
                </div>
                <div className="service-metrics">
                  <div className="metric">
                    <span className="label">CPU:</span>
                    <span className="value">{service.cpu}%</span>
                  </div>
                  <div className="metric">
                    <span className="label">Memory:</span>
                    <span className="value">{service.memory}MB</span>
                  </div>
                  <div className="metric">
                    <span className="label">Uptime:</span>
                    <span className="value">{formatUptime(Math.floor(Math.random() * 720) + 24)}</span>
                  </div>
                  <div className="metric">
                    <span className="label">Status:</span>
                    <span className="value">{service.status}</span>
                  </div>
                </div>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </div>
      </AlertsPanel>

      {/* Alerts Panel */}
      <AlertsPanel>
        <AlertsHeader>
          <div className="header-content">
            <h3>
              <span className="icon">🚨</span>
              System Alerts
            </h3>
            <div className="alert-count">{alerts.length} Active</div>
          </div>
        </AlertsHeader>

        <AlertsList>
          {alerts.map((alert) => (
            <div key={alert.id} className="alert-item">
              <div className={`alert-icon ${alert.type}`}>
                {alert.type === 'critical' ? '⚠️' : alert.type === 'warning' ? '🟡' : 'ℹ️'}
              </div>
              <div className="alert-content">
                <div className="alert-title">{alert.title}</div>
                <div className="alert-message">{alert.message}</div>
                <div className="alert-time">{alert.time}</div>
              </div>
              <div className="alert-actions">
                <button
                  className="dismiss"
                  onClick={() => handleAlertAction(alert.id, 'dismiss')}
                >
                  Dismiss
                </button>
                {alert.type !== 'info' && (
                  <button
                    className="resolve"
                    onClick={() => handleAlertAction(alert.id, 'resolve')}
                  >
                    Resolve
                  </button>
                )}
              </div>
            </div>
          ))}

          {alerts.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: palette.muted
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
              <h3>All Clear</h3>
              <p>No active alerts at this time.</p>
            </div>
          )}
        </AlertsList>
      </AlertsPanel>
    </MonitoringContainer>
  );
};

export default SystemMonitoring; 
